/* eslint-disable camelcase */
/* eslint-disable no-underscore-dangle */
interface GooglePlacesAutoCompleteOptions {
  onReady: (ready: boolean) => void;
}

export type ParsedDetailsResult = {
  line1: string;
  line2?: string;
  city: string;
  province: string;
  postalCode: string;
};

class GooglePlacesAutoComplete {
  private _SESSION_TOKEN = new google.maps.places.AutocompleteSessionToken();

  private _LOCATION = new google.maps.LatLng({ lat: 43.6532, lng: -79.3832 });

  private _autoCompleteService: google.maps.places.AutocompleteService;

  private _placeDetailsService: google.maps.places.PlacesService;

  public onReady: (ready: boolean) => void;

  public isReady: boolean;

  constructor({ onReady }: GooglePlacesAutoCompleteOptions) {
    this.onReady = onReady;

    this.initAutocomplete();
  }

  public initAutocomplete = () => {
    this._autoCompleteService = new google.maps.places.AutocompleteService();
    this._placeDetailsService = new google.maps.places.PlacesService(
      document.createElement('div'),
    );

    this.isReady = true;
    this.onReady(true);
  };

  public getPlaces = async (
    input: string,
    type: 'address' | 'establishment' = 'address',
  ) => {
    const options: google.maps.places.AutocompletionRequest = {
      input,
      location: this._LOCATION,
      radius: 50000,
      sessionToken: this._SESSION_TOKEN,
      types: [type],
    };

    return new Promise<google.maps.places.AutocompletePrediction[]>(
      (resolve, reject) => {
        this._autoCompleteService.getPlacePredictions(
          options,
          (result, status) => {
            if (
              [
                google.maps.places.PlacesServiceStatus.INVALID_REQUEST,
                google.maps.places.PlacesServiceStatus.REQUEST_DENIED,
                google.maps.places.PlacesServiceStatus.UNKNOWN_ERROR,
                google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT,
              ].includes(status)
            )
              reject(status);

            resolve(result);
          },
        );
      },
    );
  };

  public getPlaceDetails = async (placeId: string) => {
    const options: google.maps.places.PlaceDetailsRequest = {
      placeId,
      fields: ['address_component'],
      sessionToken: this._SESSION_TOKEN,
    };

    return new Promise<ParsedDetailsResult>((resolve, reject) => {
      this._placeDetailsService.getDetails(options, (result, status) => {
        if (
          [
            google.maps.places.PlacesServiceStatus.INVALID_REQUEST,
            google.maps.places.PlacesServiceStatus.REQUEST_DENIED,
            google.maps.places.PlacesServiceStatus.UNKNOWN_ERROR,
            google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT,
          ].includes(status)
        )
          reject(status);

        resolve(this._parseAddressComponents(result.address_components));
      });
    });
  };

  private _parseAddressComponents = (
    addressComponents: google.maps.places.PlaceResult['address_components'],
  ) => {
    const result: ParsedDetailsResult = {
      line1: '',
      line2: '',
      city: '',
      province: '',
      postalCode: '',
    };

    let streetNumber;
    let streetName;

    addressComponents.forEach(({ long_name, short_name, types }) => {
      if (types.includes('street_number')) streetNumber = long_name;
      if (types.includes('route')) streetName = short_name;
      if (types.includes('locality')) result.city = long_name;
      if (types.includes('administrative_area_level_1'))
        result.province = short_name;
      if (types.includes('postal_code')) result.postalCode = long_name;
    });

    result.line1 = `${streetNumber || ''} ${streetName || ''}`.trim();

    return result;
  };
}

export default GooglePlacesAutoComplete;
